/**
 * mintCore.ts
 * CruzFind Deterministic License Generation Core
 *
 * All activation key outputs are routed through this unified controller.
 * Hardware fingerprint binding uses SHA-256 so outputs are reproducible
 * from the same inputs but cannot be reverse-engineered.
 */

const CRUZFIND_MINT_SEED = 'cruzfind-mint-seed-2026';

export interface MintInput {
  /** Client hardware fingerprint or distributor account ID */
  hardwareFp: string;
  /** Optional PHP price tier used in checksum derivation (e.g. 299, 1499) */
  priceTier?: number;
  /** Optional sequential index for bulk batch generation */
  index?: number;
}

export interface MintOutput {
  /** The formatted activation key string ready for display */
  key: string;
  /** ISO-8601 timestamp of generation */
  generatedAt: string;
  /** The price tier that was bound into the key, if any */
  priceTier?: number;
}

/**
 * Derives a 6-character uppercase alphanumeric segment from an arbitrary
 * SHA-256 digest slice.
 */
async function deriveSegment(input: string, suffix: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(`${input}::${suffix}::${CRUZFIND_MINT_SEED}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hexString = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  const hashNum = BigInt('0x' + hexString);
  const base36 = hashNum.toString(36).toUpperCase();
  return base36.slice(0, 6).padEnd(6, '0');
}

/**
 * Generates a deterministic, hardware-bound activation license key.
 *
 * Output format:  CF-[RAND_SEGMENT]-[CHECK_SEGMENT]
 * Example:        CF-ON58KT-LSZRJX
 *
 * The key is reproducible given the same inputs, and the check segment
 * cryptographically binds the rand segment + fingerprint + price tier.
 */
export async function generateDeterministicLicense(input: MintInput): Promise<MintOutput> {
  const normalized = input.hardwareFp.trim().toUpperCase();
  const indexTag = input.index !== undefined ? `::${input.index}` : '';
  const priceTag = input.priceTier !== undefined ? `::${input.priceTier}` : '';

  // Derive the random (seed) segment from the fingerprint + index
  const randSegment = await deriveSegment(`${normalized}${indexTag}`, 'rand');

  // Derive the check segment from the rand segment + price tier binding
  const checkSegment = await deriveSegment(`${normalized}::${randSegment}${priceTag}`, 'check');

  const key = `CF-${randSegment}-${checkSegment}`;

  return {
    key,
    generatedAt: new Date().toISOString(),
    priceTier: input.priceTier,
  };
}

/**
 * Verifies whether a submitted activation token was generated for a specific
 * identity string (TIN, business name, or hardware fingerprint) and price tier.
 *
 * Expected token format:  CF-[RAND_SEGMENT]-[CHECK_SEGMENT]
 *
 * The rand segment is extracted from the token and re-used to re-derive the
 * expected check segment. If it matches the submitted check segment the token
 * is authentic.
 */
export async function verifyDeterministicLicense(
  submittedToken: string,
  identityKey: string,
  priceTier?: number,
): Promise<boolean> {
  const normalized = submittedToken.trim().toUpperCase();

  // Expect format: CF-XXXXXX-XXXXXX
  const parts = normalized.split('-');
  if (parts.length !== 3 || parts[0] !== 'CF') return false;

  const randSegment = parts[1];
  const submittedCheck = parts[2];
  if (!randSegment || !submittedCheck) return false;

  const normalizedId = identityKey.trim().toUpperCase();
  const priceTag = priceTier !== undefined ? `::${priceTier}` : '';

  // Re-derive the expected check segment from the rand segment + identity + price
  const expectedCheck = await deriveSegment(
    `${normalizedId}::${randSegment}${priceTag}`,
    'check',
  );

  return submittedCheck === expectedCheck;
}
